import React, { useState } from 'react';
import './UserReward.css';
import { Search, Phone, AlertCircle, CheckCircle, Loader2, User, Award, ArrowRight, ShoppingBag, Info, Receipt, DollarSign } from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from "../utils/auditClient";

const redeemNotes = [
  "Reward points earned have a 180 day validity period from the day it has been credited. Please redeem it prior to the expiry of Reward points.",
  "Reward points can be redeemed as per the following slab: ",
];
const redeemSmallNotes = [
  "Purchase value of Rs 1000 to Rs 1999 – 50% of total available reward points",
  "Purchase value of Rs 2,000 to Rs 4,999 – 75% of total available reward points",
  "Purchase value of Rs 5,000 and above – 100% of total available reward points",
];

/** ---------------------------
 * Profile card: shows identity + that profile's current points (display-only)
 * --------------------------- */
const ProfileRewardCard = ({ profile }) => {
  return (
    <div className="urp-card">
      <div className="urp-card-header">
        <div className="urp-user-info">
          <div className="urp-avatar" style={{ background: `linear-gradient(135deg, #003365, #006bb3)` }}>
            <User className="urp-avatar-icon" />
          </div>
          <div className="urp-user-details">
            <h3 className="urp-user-name">{profile.playerName}</h3>
          </div>
        </div>

        <div className="urp-points-display">
          <div className="urp-points-icon">
            <Award className="urp-award-icon" />
          </div>
          <div className="urp-points-content">
            <span className="urp-points-label">Profile Points</span>
            <span className="urp-points-value">{Number(profile.rewardBalance || 0).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const RedeemRewardsCard = ({
  profiles,
  totalRewards,
  isProcessing,
  isVerified,
  isSendingOtp,
  isVerifyingOtp,
  otp,
  setOtp,
  showOtpField,
  onSendOtp,
  onVerifyOtp,
  onRedeem,
  redeemPoints,
  onPointsEnter,
  selectedForRedemption,
  setSelectedForRedemption,
  phoneNumber,
}) => {
  const [purchaseValue, setPurchaseValue] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [error, setError] = useState('');
  const [autoNote, setAutoNote] = useState(''); // helper line showing the math

  const selectedProfile = profiles.find(p => p.userId === selectedForRedemption) || profiles[0];
  const userId = selectedProfile?.userId;
  const redeemAmount = redeemPoints[userId] ?? '';

  // pick default selection if not set (no UI dropdown)
  React.useEffect(() => {
    if (profiles.length && !selectedForRedemption) {
      setSelectedForRedemption(profiles[0].userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, selectedForRedemption]);

  // slab % helper
  const getSlabPercent = (pv) => {
    if (pv >= 5000) return 1.0;      // 100%
    if (pv >= 2000) return 0.75;     // 75%
    if (pv >= 1000) return 0.50;     // 50%
    return 0;
  };

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  // central auto-calc based on PV and GLOBAL total
  const recalcAutoPoints = (pv) => {
    setError('');
    setAutoNote('');

    const pvNum = parseFloat(pv);
    if (isNaN(pvNum) || pvNum < 0) {
      onPointsEnter(userId, '');
      if (pv !== '') setError('Please enter a valid purchase value');
      return;
    }

    const percent = getSlabPercent(pvNum);
    if (pvNum > 0 && percent === 0) {
      onPointsEnter(userId, '');
      setError('Minimum purchase ₹1000 to redeem points');
      return;
    }

    if (pvNum === 0) {
      onPointsEnter(userId, '');
      setAutoNote('');
      return;
    }

    const total = Number(totalRewards || 0);
    const allowedBySlab = round2(total * percent); // global
    const auto = round2(allowedBySlab);

    if (auto <= 0) {
      onPointsEnter(userId, '');
      setAutoNote('');
      return;
    }

    onPointsEnter(userId, auto.toFixed(2));
    const percentLabel = percent === 1 ? '100%' : percent === 0.75 ? '75%' : '50%';
    const note = `${percentLabel} of total ${round2(total).toFixed(2)} pts = ${allowedBySlab.toFixed(2)} pts`;
    setAutoNote(note);
  };

  // hook into PV / selection / total changes
  React.useEffect(() => {
    if (!userId) return;
    if (purchaseValue === '') {
      onPointsEnter(userId, '');
      setAutoNote('');
      return;
    }
    recalcAutoPoints(purchaseValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseValue, userId, totalRewards, selectedForRedemption]);

  const handlePurchaseChange = (value) => {
    if (value === '') {
      setPurchaseValue('');
      onPointsEnter(userId, '');
      setAutoNote('');
      setError('');
      return;
    }
    setPurchaseValue(value);
  };

  const handleRedeemClick = async () => {
    const pointsNum = parseFloat(redeemAmount);
    const purchaseNum = parseFloat(purchaseValue);

    if (!userId) { setError('Please select a profile'); return; }
    if (isNaN(purchaseNum) || purchaseNum <= 0) { setError('Please enter a valid purchase value'); return; }
    if (purchaseNum < 1000) { setError('Minimum purchase ₹1000 to redeem points'); return; }
    if (!invoiceNumber.trim()) { setError('Please enter Vasy invoice number'); return; }
    if (isNaN(pointsNum) || pointsNum <= 0) { setError('Auto-calculation did not produce a redeemable amount'); return; }

    // GLOBAL cap: cannot exceed totalRewards
    const globalCap = Number(totalRewards || 0);
    if (pointsNum > globalCap) { setError(`Cannot exceed available points (${globalCap.toFixed(2)})`); return; }

    const ok = await onRedeem(
      userId,
      pointsNum,
      selectedProfile?.academyId ?? null,
      selectedProfile,
      purchaseValue,
      invoiceNumber
    );

    if (ok) {
      setPurchaseValue('');
      setInvoiceNumber('');
      setError('');
      onPointsEnter(userId, '');
      setAutoNote('');
    }
  };

  const isDisabled =
    !redeemAmount ||
    parseFloat(redeemAmount) <= 0 ||
    !purchaseValue ||
    !invoiceNumber ||
    isProcessing ||
    !!error;

  const canRedeem = !isDisabled && isVerified && selectedForRedemption === userId;

  return (
    <div className="urp-card">
      <div className="urp-card-header" style={{ alignItems: 'flex-start' }}>
        <div className="urp-user-info">
          <div className="urp-avatar" style={{ background: `linear-gradient(135deg, #5b0060, #9b00b3)` }}>
            <ShoppingBag className="urp-avatar-icon" />
          </div>
          <div className="urp-user-details">
            <h3 className="urp-user-name">Redeem Rewards</h3>
            <p className="urp-search-description">Redemption uses the global balance across profiles</p>
          </div>
        </div>

        <div className="urp-points-display">
          <div className="urp-points-icon">
            <Award className="urp-award-icon" />
          </div>
          <div className="urp-points-content">
            <span className="urp-points-label">Total Reward Points (Global)</span>
            <span className="urp-points-value">{Number(totalRewards || 0).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Redeem form */}
      <div className="urp-redeem-section">
        {/* Purchase Value */}
        <div className="urp-input-group">
          <label className="urp-input-label">
            <DollarSign size={16} />
            Purchase Value
          </label>
          <div className="urp-input-container">
            <input
              type="number"
              min="0"
              step="0.01"
              value={purchaseValue}
              onChange={(e) => handlePurchaseChange(e.target.value)}
              placeholder="Enter purchase value"
              disabled={isProcessing}
              className="urp-redeem-input"
              onWheel={(e) => e.target.blur()}
            />
            <span className="urp-input-suffix">₹</span>
          </div>
        </div>

        {/* Invoice Number */}
        <div className="urp-input-group">
          <label className="urp-input-label">
            <Receipt size={16} />
            Vasy Invoice Number
          </label>
          <div className="urp-input-container">
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Enter invoice number"
              disabled={isProcessing}
              className="urp-redeem-input"
            />
          </div>
        </div>

        {/* Points to Redeem (auto-calculated, read-only) */}
        <div className="urp-input-group">
          <label className="urp-input-label">
            <Award size={16} />
            Points to Redeem
          </label>
          <div className="urp-input-container">
            <input
              type="number"
              min="0"
              step="0.01"
              value={redeemAmount}
              readOnly
              className="urp-redeem-input urp-redeem-input--readonly"
            />
            <span className="urp-input-suffix">points</span>
          </div>
        </div>

        {/* OTP flow (global-balance gating) */}
        {redeemAmount &&
          parseFloat(redeemAmount) > 0 &&
          Number(totalRewards || 0) > 0 &&
          !isVerified && (
            <div className="urp-card-otp-section" style={{ marginTop: 12 }}>
              {!showOtpField ? (
                <button
                  onClick={() => onSendOtp(userId)}
                  disabled={isSendingOtp || !redeemAmount || !purchaseValue || !invoiceNumber}
                  className="urp-verify-request-btn"
                >
                  {isSendingOtp ? (
                    <>
                      <Loader2 className="urp-btn-spinner" />
                      <span>Send OTP</span>
                    </>
                  ) : (
                    <>
                      <Phone className="urp-search-icon" />
                      <span>Send OTP</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="urp-card-otp-inputs">
                  <div className="urp-otp-input-wrapper">
                    <div className="urp-otp-label">
                      <Phone size={16} />
                      <span>Enter OTP sent to {phoneNumber ? `+91${phoneNumber}` : 'mobile'}</span>
                    </div>
                    <input
                      type="tel"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      disabled={isVerifyingOtp}
                      className="urp-otp-input"
                      maxLength="6"
                    />
                    <div className="urp-otp-dots">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div key={index} className={`urp-otp-dot ${index < otp.length ? 'filled' : ''}`} />
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={onVerifyOtp}
                    disabled={isVerifyingOtp || !otp || otp.length !== 6}
                    className="urp-verify-otp-btn"
                  >
                    {isVerifyingOtp ? (
                      <>
                        <Loader2 className="urp-search-spinner" />
                        <span>Verify OTP</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="urp-search-icon" />
                        <span>Verify OTP</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

        {/* Redeem button */}
        {redeemAmount && parseFloat(redeemAmount) > 0 && isVerified && selectedForRedemption === userId && (
          <div className="urp-redeem-action">
            <button
              onClick={handleRedeemClick}
              disabled={!canRedeem || isProcessing}
              className="urp-redeem-confirm-btn"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="urp-btn-spinner" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>Redeem</span>
                  <ArrowRight className="urp-btn-arrow" />
                </>
              )}
            </button>
          </div>
        )}

        {error && (
          <div className="urp-error-message" style={{ marginTop: 10 }}>
            <AlertCircle className="urp-error-icon" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const UserReward = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState({});
  const [selectedForRedemption, setSelectedForRedemption] = useState(null);
  const [showOtpField, setShowOtpField] = useState(false);
  const [totalRewards, setTotalRewards] = useState(0); // GLOBAL balance from API

  const token = localStorage.getItem('auth-token');

  const showToast = (icon, title, text) => {
    Swal.fire({
      icon,
      title,
      text,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: '#fff',
      color: '#333',
      customClass: { popup: 'custom-swal-popup' }
    });
  };

  const handleSendOtp = async (userId) => {
    if (!phoneNumber.trim() || phoneNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    const points = redeemPoints[userId];
    if (!points || parseFloat(points) <= 0) {
      setError('Points not eligible for this purchase. Check purchase value (min ₹1000).');
      showToast('warning', 'Points Not Eligible', 'Adjust purchase value to meet slab rules.');
      return;
    }

    setIsSendingOtp(true);
    setError('');
    setSelectedForRedemption(userId);

    try {
      const formattedContactNumber = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`;

      const response = await axios.post(process.env.REACT_APP_API_URL_OTP_SEND, {
        data: { phone_number: formattedContactNumber, has_firebase_check: false },
      });

      if (response.data.success) {
        setShowOtpField(true);
        setIsVerified(false);
        setOtp('');
        showToast('success', 'OTP Sent', `OTP sent to ${formattedContactNumber}`);
      } else {
        setError(response.data.message || 'Failed to send OTP. Please try again.');
        showToast('error', 'Failed', response.data.message || 'Failed to send OTP');
      }
    } catch (error) {
      setError('Failed to send OTP. Please try again.');
      showToast('error', 'Error', 'Failed to send OTP. Please try again.');
      console.error('OTP Send Error:', error);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      showToast('warning', 'Invalid OTP', 'Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setError('');

    try {
      const formattedContactNumber = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`;

      const response = await axios.post(process.env.REACT_APP_API_URL_OTP_VERIFY, {
        data: { phone_number: formattedContactNumber, has_firebase_check: false, otp },
      });

      if (response.data.success) {
        setIsVerified(true);
        setShowOtpField(false);
        showToast('success', 'Verified', 'OTP verified successfully! You can now redeem points.');
      } else {
        const errorMsg = response.data.error_message || response.data.message || 'Invalid OTP. Please try again.';
        setError(errorMsg);
        showToast('error', 'Verification Failed', errorMsg);
      }
    } catch (error) {
      const serverError =
        error.response?.data?.error_message ||
        error.response?.data?.message ||
        'Failed to verify OTP. Please try again.';
      setError(serverError);
      showToast('error', 'Error', serverError);
      console.error('OTP Verify Error:', error);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const fetchShopData = async (formattedContactNumber) => {
    setIsSearching(true);
    setError('');
    setProfiles([]);
    setRedeemPoints({});
    setSelectedForRedemption(null);
    setIsVerified(false);
    setShowOtpField(false);
    setOtp('');
    setTotalRewards(0);

    try {
      const mobileNumber = formattedContactNumber.replace('+91', '');

      const shopResponse = await axios.get(
        `${process.env.REACT_APP_API_PLAYER_SHOP_NEW}?mobileNumber=${mobileNumber}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const shopData = shopResponse.data;

      // GLOBAL balance from API
      setTotalRewards(Number(shopData?.totalRewardBalance || 0));

      // Profiles for display: include per-profile rewardBalance for showing on cards only
      const userProfiles = Object.keys(shopData)
        .filter(key => !isNaN(Number(key)))
        .map(key => {
          const playerData = shopData[key];
          const player = playerData.player;
          const user = player.user;

          const primaryBatch = player.playerBatches?.find(batch => batch.primary) ||
            player.playerBatches?.[0];

          const rewardBalance =
            (playerData?.rewardBalance ??
              playerData?.wallet?.rewardBalance ??
              playerData?.reward_points_balance ??
              0);

          return {
            id: user.id,
            userId: user.id,
            playerId: player.id,
            playerName: user.name,
            phoneNumber: formattedContactNumber,
            rewardBalance: Number(rewardBalance) || 0, // display-only
            academyId: primaryBatch?.academyId || null,
            academyName: primaryBatch?.academyName || 'N/A',
            transactionType: playerData.transactionType,
            createdAt: player.createdAt,
            updatedAt: player.updatedAt,
            userType: user.user_type,
          };
        });

      if (userProfiles.length === 0) {
        setError('No user profiles found for this phone number');
        showToast('info', 'No Profiles', 'No user profiles found for this phone number');
      } else {
        setProfiles(userProfiles);
        setSelectedForRedemption(userProfiles[0].userId);
        showToast('success', 'Profiles Found', `Found ${userProfiles.length} user profile(s)`);
      }
    } catch (error) {
      console.error('Fetch Shop Data Error:', error);
      setError('Failed to fetch user data. Please try again.');
      showToast('error', 'Error', 'Failed to fetch user data. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handlePointsEnter = (userId, points) => {
    setRedeemPoints(prev => ({ ...prev, [userId]: points }));
  };

  const handleRedeem = async (userId, points, academyId, profile, purchaseValue, invoiceNumber) => {
    if (!isVerified || selectedForRedemption !== userId) {
      setError('Please verify OTP before redeeming points');
      Swal.fire({ icon: 'warning', title: 'OTP Required', text: 'Please verify OTP before redeeming points', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return false;
    }

    setIsProcessing(true);
    setError('');

    try {
      // GLOBAL diff preview only
      const oldTotal = Number(totalRewards || 0);
      const newTotal = Math.max(0, oldTotal - points);

      const { html } = buildDiffPreview(
        { totalRewardBalance: oldTotal },
        { totalRewardBalance: newTotal }
      );

      // Build arrays of ALL profile ids (numbers, not strings)
      const allUserIds = Array.from(
        new Set((profiles || []).map(p => p.userId).filter(id => id != null))
      );
      const allPlayerIds = Array.from(
        new Set((profiles || []).map(p => p.playerId).filter(id => id != null))
      );

      const confirm = await Swal.fire({
        title: "Confirm Points Redemption?",
        html: `
        <div style="text-align: left;">
          <p><strong>User:</strong> ${profile.playerName}</p>
          <p><strong>Phone:</strong> ${profile.phoneNumber}</p>
          <p><strong>Purchase Value:</strong> ₹${parseFloat(purchaseValue).toFixed(2)}</p>
          <p><strong>Invoice Number:</strong> ${invoiceNumber}</p>
          <p><strong>Points to redeem:</strong> ${Number(points).toFixed(2)}</p>
          <p><strong>Total balance change:</strong> ${oldTotal.toFixed(2)} → ${newTotal.toFixed(2)}</p>
          <p><strong>Profiles in request:</strong> ${allUserIds.length}</p>
          ${html}
        </div>
      `,
        showCancelButton: true,
        confirmButtonText: "Redeem Points",
        cancelButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#d33",
      });

      if (!confirm.isConfirmed) {
        setIsProcessing(false);
        return false;
      }

      // ARRAY payload (ids are numbers)
      const payload = {
        ids: allUserIds,            // ← array of user/profile ids
        points: Number(points),     // keep numeric
        type: "DEBIT",
        purchaseValue: String(purchaseValue),
        invoiceNumber: String(invoiceNumber)
      };

      console.log('Redemption payload:', payload);

      const redemptionResponse = await axios.post(
        process.env.REACT_APP_API_PLAYER_SHOP_NEW,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (redemptionResponse.status === 200 || redemptionResponse.data?.success) {
        // Update only the GLOBAL total
        setTotalRewards(prev => Math.max(0, Number(prev) - Number(points)));

        Swal.fire({ icon: 'success', title: 'Redemption Successful', text: `${points} points redeemed for ${profile.playerName}!`, toast: true, position: 'top-end', timer: 2500, showConfirmButton: false });

        // clear input state
        setRedeemPoints(prev => ({ ...prev, [userId]: '' }));
        setIsVerified(false);
        setSelectedForRedemption(null);
        setOtp('');

        fireAudit({
          entity: "user_reward",
          affected_id: userId,
          action: "update",
          businessApi: process.env.REACT_APP_API_PLAYER_SHOP_NEW,
          businessMethod: "POST",
          oldPayload: { userId, totalRewardBalance: oldTotal, playerName: profile.playerName, phoneNumber: profile.phoneNumber },
          newPayload: {
            userId,
            totalRewardBalance: newTotal,
            playerName: profile.playerName,
            phoneNumber: profile.phoneNumber,
            pointsRedeemed: points,
            purchaseValue,
            invoiceNumber,
            ids: allUserIds,
            player_ids: allPlayerIds
          },
          section: 'Centre Management',
          subSection: 'User Reward',
          extra: { page: "UserReward", operation: "points_redeem", redemption_type: "DEBIT" },
        });
        const formattedContactNumber = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`;
        await fetchShopData(formattedContactNumber);

        return true;
      } else {
        throw new Error(redemptionResponse.data?.message || 'Redemption failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.error_message ||
        'Failed to redeem points. Please tryagain.';
      setError(errorMessage);
      Swal.fire({ icon: 'error', title: 'Redemption Failed', text: errorMessage, toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      console.error('Redemption error:', err);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };


  const handleKeyPress = (e) => { if (e.key === 'Enter') handleSearch(); };

  const handlePhoneChange = (value) => {
    const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(cleanedValue);

    if (cleanedValue !== phoneNumber) {
      setProfiles([]);
      setRedeemPoints({});
      setSelectedForRedemption(null);
      setIsVerified(false);
      setShowOtpField(false);
      setOtp('');
      setTotalRewards(0);
    }
  };

  const resetSearch = () => {
    setPhoneNumber('');
    setOtp('');
    setProfiles([]);
    setShowOtpField(false);
    setIsVerified(false);
    setError('');
    setSuccessMessage('');
    setRedeemPoints({});
    setSelectedForRedemption(null);
    setTotalRewards(0);
    Swal.fire({ icon: 'info', title: 'Reset', text: 'Ready to search another number', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
  };

  const handleSearch = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      Swal.fire({ icon: 'warning', title: 'Missing Info', text: 'Please enter a phone number', toast: true, position: 'top-end', timer: 2500, showConfirmButton: false });
      return;
    }
    if (phoneNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      Swal.fire({ icon: 'warning', title: 'Invalid Number', text: 'Please enter a valid 10-digit mobile number', toast: true, position: 'top-end', timer: 2500, showConfirmButton: false });
      return;
    }
    const formattedContactNumber = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`;
    await fetchShopData(formattedContactNumber);
  };

  return (
    <div className="enrollment-container">
      <div className="urp-content">
        {/* Search Section */}
        <div className="urp-search-section">
          <div className="urp-search-card">
            <div className="urp-search-header">
              <h2 className="urp-search-title">Find Customer</h2>
              <p className="urp-search-description">Enter phone number to search reward profiles</p>
            </div>

            <div className="urp-search-controls">
              <div className="urp-phone-input-wrapper">
                <Phone className="urp-phone-icon" />
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter customer phone number..."
                  disabled={isSearching || isSendingOtp || isVerifyingOtp}
                  className="urp-phone-input"
                  maxLength="10"
                />
              </div>

              <button
                onClick={handleSearch}
                disabled={isSearching || !phoneNumber.trim() || phoneNumber.length !== 10}
                className="urp-search-btn"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="urp-search-spinner" />
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <Search className="urp-search-icon" />
                    <span>Find Profiles</span>
                  </>
                )}
              </button>
            </div>

            {/* Reset Button */}
            {profiles.length > 0 && (
              <div className="urp-reset-section">
                <button onClick={resetSearch} className="urp-reset-btn">
                  <Phone size={16} />
                  <span>Search Another Number</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Redemption Notes Section */}
        <div className="urp-notes-section">
          <div className="urp-notes-card">
            <div className="urp-notes-header">
              <Info className="urp-notes-icon" />
              <h3 className="urp-notes-title">Reward Points Information</h3>
            </div>
            <div className="urp-notes-content">
              {redeemNotes.map((note, index) => (
                <div key={index} className="urp-note-item">
                  <AlertCircle size={14} className="urp-note-item-icon" />
                  <span>{note}</span>
                </div>
              ))}
              <div className="urp-slabs-section">
                <h4 className="urp-slabs-title">Redemption Slabs:</h4>
                {redeemSmallNotes.map((slab, index) => (
                  <div key={index} className="urp-slab-item">
                    <div className="urp-slab-bullet"></div>
                    <span>{slab}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Global Redeem Rewards Card */}
        {profiles.length > 0 && (
          <div className="urp-results-section" style={{ marginTop: 0, marginBottom: 24 }}>
            <RedeemRewardsCard
              profiles={profiles}
              totalRewards={totalRewards}
              isProcessing={isProcessing}
              isVerified={isVerified}
              isSendingOtp={isSendingOtp}
              isVerifyingOtp={isVerifyingOtp}
              otp={otp}
              setOtp={setOtp}
              showOtpField={showOtpField && !!selectedForRedemption}
              onSendOtp={handleSendOtp}
              onVerifyOtp={handleVerifyOtp}
              onRedeem={handleRedeem}
              redeemPoints={redeemPoints}
              onPointsEnter={handlePointsEnter}
              selectedForRedemption={selectedForRedemption}
              setSelectedForRedemption={setSelectedForRedemption}
              phoneNumber={phoneNumber}
            />
          </div>
        )}

        {/* Results: Profiles list (shows per-profile points, display-only) */}
        {profiles.length > 0 && (
          <div className="urp-results-section">
            <div className="urp-profiles-grid">
              {profiles.map((profile) => (
                <ProfileRewardCard key={profile.id} profile={profile} />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isSearching && profiles.length === 0 && !error && phoneNumber === '' && (
          <div className="urp-empty-state">
            <div className="urp-empty-illustration">
              <div className="urp-empty-icon">
                <ShoppingBag className="urp-empty-award" />
              </div>
              <div className="urp-empty-glow"></div>
            </div>
            <div className="urp-empty-content">
              <h3 className="urp-empty-title">Ready to Redeem Rewards?</h3>
              <p className="urp-empty-description">
                Enter a customer's phone number above to discover their reward points and redeem them.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserReward;
